import { isDefined } from "@clipboard-health/util-ts";
import { type TimesheetEntry } from "@src/appV2/redesign/WorkerShifts/Timekeeping/Timesheet/hooks/useTimesheetEntry";
import { calculateHasPendingHoursAdjustment } from "@src/appV2/redesign/WorkerShifts/Timekeeping/Timesheet/utils/calculateHasPendingHoursAdjustment";
import { isAfter, isBefore, isEqual, parseISO } from "date-fns";
import { useMemo } from "react";

import { useGetProvisionalExtraWorkedTimeRequest } from "../api/useGetProvisionalExtraWorkedTimeRequest";

interface UsePendingHoursAdjustmentParams {
  shiftId: string | undefined;
  isMissedPunchRequestEnabledV2: boolean;
  /** The original recorded timesheet (hcp_clock_in/out). Used as the "verified" baseline. */
  formTimesheet: TimesheetEntry | undefined;
  /** The currently displayed clock-in time (may equal the form value if no adjustment). */
  formClockInTime: Date | undefined;
  formClockOutTime: Date | undefined;
  formBreakStartTime: Date | undefined;
  formBreakEndTime: Date | undefined;
  /**
   * Fallback clock-in time used as the "original" baseline when formTimesheet is undefined
   * (e.g. unverified shifts with no IP clock-in). Typically the scheduled shift start ISO string.
   */
  recordedClockInTime?: string;
}

export interface UsePendingHoursAdjustmentResult {
  /** Date parsed from the provisional extra worked time request's start, or undefined when none exists. */
  adjustedClockInTime: Date | undefined;
  /** The provisional extra worked time request entity, if one exists for this shift. */
  existingRequest: { id: string; attributes: { start: string; reason: string } } | undefined;
  /**
   * True when `isMissedPunchRequestEnabledV2` is on AND the adjusted timesheet claims more paid
   * minutes than the verified (original) timesheet. Use this to gate both the alert banner and the
   * Verified/Pending breakdown in TimesheetTimeEntries.
   */
  hasPendingHoursAdjustment: boolean;
  /**
   * Pass as `originalClockInTime` on TimesheetTimeEntries.
   * Only set when the feature is on and a clock-in adjustment exists.
   */
  originalClockInTime: Date | undefined;
  /**
   * Pass as `originalClockOutTime` on TimesheetTimeEntries.
   * Only set when the feature is on and the form clock-out is later than the recorded value.
   */
  originalClockOutTime: Date | undefined;
  /**
   * True when the feature is on and the break times differ from the recorded timesheet.
   * Use this to gate the `originalBreakDisplay` prop — format the string in the caller.
   */
  hasBreakAdjustment: boolean;
  /**
   * Pass as `verifiedTimesheet` on TimesheetTimeEntries.
   * Only set when the feature is on and more hours are being claimed.
   */
  verifiedTimesheetForEntries: TimesheetEntry | undefined;
}

function areOptionalDatesEqual(a: Date | undefined, b: Date | undefined): boolean {
  if (!isDefined(a) && !isDefined(b)) {
    return true;
  }

  if (!isDefined(a) || !isDefined(b)) {
    return false;
  }

  return isEqual(a, b);
}

function resolveOriginalClockInTime(params: {
  isMissedPunchRequestEnabledV2: boolean;
  adjustedClockInTime: Date | undefined;
  formTimesheet: TimesheetEntry | undefined;
  formClockInTime: Date | undefined;
  recordedClockInTime: Date | undefined;
}): Date | undefined {
  const {
    isMissedPunchRequestEnabledV2,
    adjustedClockInTime,
    formTimesheet,
    formClockInTime,
    recordedClockInTime,
  } = params;

  if (!isMissedPunchRequestEnabledV2) {
    return undefined;
  }

  const isEarlierThanRecorded =
    isDefined(formTimesheet) &&
    isDefined(formClockInTime) &&
    isBefore(formClockInTime, formTimesheet.clockInTime);

  if (isDefined(adjustedClockInTime) || isEarlierThanRecorded) {
    return formTimesheet?.clockInTime ?? recordedClockInTime;
  }

  return undefined;
}

/**
 * Consolidates the pending-hours-adjustment data fetching and derived state
 */
export function usePendingHoursAdjustment({
  shiftId,
  isMissedPunchRequestEnabledV2,
  formTimesheet,
  formClockInTime,
  formClockOutTime,
  formBreakStartTime,
  formBreakEndTime,
  recordedClockInTime: recordedClockInTimeIso,
}: UsePendingHoursAdjustmentParams): UsePendingHoursAdjustmentResult {
  const recordedClockInTime = useMemo(
    () =>
      formTimesheet?.clockInTime ??
      (recordedClockInTimeIso ? parseISO(recordedClockInTimeIso) : undefined),
    [formTimesheet?.clockInTime, recordedClockInTimeIso]
  );
  const { data: provisionalExtraWorkedTimeData } = useGetProvisionalExtraWorkedTimeRequest(
    shiftId ?? "",
    { enabled: isMissedPunchRequestEnabledV2 && isDefined(shiftId) && shiftId.length > 0 }
  );

  const existingRequest = provisionalExtraWorkedTimeData?.data[0];

  const adjustedClockInTime = useMemo(
    () =>
      existingRequest?.attributes.start ? parseISO(existingRequest.attributes.start) : undefined,
    [existingRequest?.attributes.start]
  );

  const displayClockInTime = adjustedClockInTime ?? formClockInTime;

  const rawHasPendingHoursAdjustment =
    isDefined(formTimesheet) && isDefined(displayClockInTime) && isDefined(formClockOutTime)
      ? calculateHasPendingHoursAdjustment({
          verifiedTimesheet: formTimesheet,
          adjustedTimesheet: {
            clockInTime: displayClockInTime,
            clockOutTime: formClockOutTime,
            breakStartTime: formBreakStartTime,
            breakEndTime: formBreakEndTime,
          },
        })
      : false;

  // Incorporate the feature flag so all callers can use hasPendingHoursAdjustment directly
  // without needing to re-apply the flag check themselves.
  const hasPendingHoursAdjustment = isMissedPunchRequestEnabledV2 && rawHasPendingHoursAdjustment;

  const originalClockOutTime =
    isMissedPunchRequestEnabledV2 &&
    isDefined(formTimesheet) &&
    isDefined(formClockOutTime) &&
    isAfter(formClockOutTime, formTimesheet.clockOutTime)
      ? formTimesheet.clockOutTime
      : undefined;

  const hasBreakAdjustment =
    isMissedPunchRequestEnabledV2 &&
    isDefined(formTimesheet) &&
    (!areOptionalDatesEqual(formTimesheet.breakStartTime, formBreakStartTime) ||
      !areOptionalDatesEqual(formTimesheet.breakEndTime, formBreakEndTime));

  return {
    adjustedClockInTime,
    existingRequest,
    hasPendingHoursAdjustment,
    originalClockInTime: resolveOriginalClockInTime({
      isMissedPunchRequestEnabledV2,
      adjustedClockInTime,
      formTimesheet,
      formClockInTime,
      recordedClockInTime,
    }),
    originalClockOutTime,
    hasBreakAdjustment,
    verifiedTimesheetForEntries: hasPendingHoursAdjustment ? formTimesheet : undefined,
  };
}
